import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from 'react-bootstrap';
import { DateTime } from 'luxon';
import { apiFetch, apiFetchRaw } from '../../../utility/fetch';
import { useLoading } from '../../../loading/LoadingContext';
import { useUserContext } from '../../../user/UserContext';
import CustomError, { CustomErrorProps } from '../../../common/components/CustomError';
import WootFbaNewJourney from './WootFbaNewJourney';
import { OrdersWootTableRow, WootAllocationExcelRow } from '../../WootOrdersImport.interfaces';
import { excelToJson } from '../../../common/common.helpers';
import RefreshSvg from '../../../common/components/svg/RefreshSvg';
import WootFbaJourneyHeader from './WootFbaJourneyHeader';
import { TabList, TabPanel, Tabs, Tab } from 'react-tabs';
import { getStatusColors, mapWootAllocationIAGs } from '../../woot.fba.helpers';
import { Inventory__c } from '../../../common/common.interfaces';
import BlobModalComponent, { BlobModalComponentProperties } from '../../../blobs/BlobModalComponent';
import JSONPretty from 'react-json-pretty';
import { CUSTOM_JSON_THEME } from '../../../common/common.constants';
import WootFbaJourneyAllocation from './WootFbaJourneyAllocation';
import WootFbaJourneyReturnPO from './WootFbaJourneyReturnPO';
import WootFbaJourneyInvoice from './WootFbaJourneyInvoice';

export default function WootFbaOrdersImport() {
  const {toggleLoading, changeStep, changeSubStep} = useLoading()
  const {userData} = useUserContext()

  const WOOT_FBA_CB_PATH = 'crystal-ball/woot/fba'

  const [wootOrders, setWootOrders]: [OrdersWootTableRow[], any] = useState(undefined)
  const [newPONumber, setNewPONumber] = useState(undefined)
  
  const [lastRefresh, setLastRefresh] = useState(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  const [showComplete, setShowComplete] = useState(false)

  const [dataError, setDataError]: [CustomErrorProps, any] = useState(undefined)

  const [blobModal, setBlobModal]: [BlobModalComponentProperties, any] = useState(undefined)

  // #region LOADING
  useEffect(() => {
    const load = async function() {
      changeStep('WOOT! FBA Orders')
      setDataError(undefined)
      let subStep = showComplete === true ? 'all' : 'incomplete'
      changeSubStep(`Loading ${subStep} journeys.`)
      toggleLoading(true)

      const filterParam = showComplete === false ?
        {key: 'filter', value: `PartitionKey eq \'woot-fba\' and status ne \'complete\'`} :
        {key: 'filter', value: `PartitionKey eq \'woot-fba\'`}

      const params = [
        {key: 'table', value: 'ordersWoot'},
        {...filterParam}
      ]

      try {
        const request = {
          endpoint: 'azureTables',
          method: 'GET',
          accessToken: userData.accessToken,
          params
        }
        const response = await apiFetch(request)
        if(!response.ok){
          setDataError({
            content: JSON.stringify({request: {...request, accessToken: 'REDACTED', headers: {Authorization: 'REDACTED'}}, response}),
            description: `Error getting data from the WOOT! orders table in Azure.`,
            title: 'WOOT! FBA Loading Failed',
            visible: true
          } as CustomErrorProps)
          toggleLoading(false)
          return
        } else {
          setWootOrders(response.body)
        }
      }catch(error){
        setDataError({
          content: JSON.stringify(error),
          description: `Error getting data from the WOOT! orders table in Azure.`,
          title: 'WOOT! FBA Loading Failed',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }
      toggleLoading(false)
    }
    load()
  }, [showComplete, lastRefresh, userData])
  // #endregion

  // #region NEW JOURNEY
  const handleNewJourneySave = useCallback(async (journey: {poNumber: string, iagShipmentName: string, pdfFile: File, excelFile: File}) => {
    setDataError({
      visible: false
    })
    
    const existing = wootOrders?.find(o => o.rowKey === journey.poNumber)
    if(existing){
      setDataError({
        content: JSON.stringify(existing),
        description: `Failed to save new jouryney because that PO Number ${journey.poNumber} already exists.`,
        title: 'WOOT! FBA Journey Already Exists',
        visible: true
      } as CustomErrorProps)
      return
    }
    toggleLoading(true)
    changeStep('Starting Journey')

    const ordersWootRow: OrdersWootTableRow = {
      partitionKey: 'woot-fba',
      rowKey: journey.poNumber,
      allocationStatus: 'pending',
      poNumber: journey.poNumber,
      createdTimestamp: DateTime.utc().toISO(),
      status: 'pending',
      //set later
      sourcePoExcelPath: undefined,
      sourcePoJsonPath: undefined,
      sourcePoPdfPath: undefined
    }

    const baseBlobPath = `${WOOT_FBA_CB_PATH}/${journey.poNumber}`
    const pdfPath = `${baseBlobPath}/${journey.pdfFile.name}`
    const excelPath = `${baseBlobPath}/${journey.excelFile.name}`

    changeSubStep('Storing PDF File.')

    try {
      const pdfResponse = await apiFetch({
        endpoint: 'blob',
        method: 'POST',
        accessToken: userData.accessToken,
        params: [
          {key: 'location', value: 'dataservices'},
          {key: 'container', value: 'storage'},
          {key: 'blobname', value: pdfPath}
        ],
        headers: {
          'Content-Type': 'application/pdf'
        },
        body: journey.pdfFile
      })
      if(!pdfResponse.ok){
        setDataError({
          content: JSON.stringify(pdfResponse),
          description: `Failed to save the PDF file.`,
          title: 'WOOT! FBA New Journey Save Failed',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }
      ordersWootRow.sourcePoPdfPath = pdfPath
    } catch(error){
      setDataError({
        content: JSON.stringify(error),
        description: `Failed to save the PDF file.`,
        title: 'WOOT! FBA New Journey Save Failed',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }     

    changeSubStep('Storing Excel File.')

    try {
      const excelResponse = await apiFetch({
        endpoint: 'blob',
        method: 'POST',
        accessToken: userData.accessToken,
        params: [
          {key: 'location', value: 'dataservices'},
          {key: 'container', value: 'storage'},
          {key: 'blobname', value: excelPath}
        ],
        headers: {
          'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        },
        body: journey.excelFile
      })
      if(!excelResponse.ok){
        setDataError({
          content: JSON.stringify(excelResponse),
          description: `Failed to save the Excel file.`,
          title: 'WOOT! FBA New Journey Save Failed',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }
      ordersWootRow.sourcePoExcelPath = excelPath
    } catch(error){
      setDataError({
        content: JSON.stringify(error),
        description: `Failed to save the Excel file.`,
        title: 'WOOT! FBA New Journey Save Failed',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep('Converting Excel to JSON then Storing.')
    try {
      const jsonPath = `${baseBlobPath}/${journey.excelFile.name.replace('.xlsx', '.json')}`
      const rows = await excelToJson<WootAllocationExcelRow>(journey.excelFile)
      const jsonResponse = await apiFetch({
        endpoint: 'blob',
        method: 'POST',
        accessToken: userData.accessToken,
        params: [
          {key: 'location', value: 'dataservices'},
          {key: 'container', value: 'storage'},
          {key: 'blobname', value: jsonPath}
        ],
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(rows)
      })
      if(!jsonResponse.ok){
        setDataError({
          content: JSON.stringify(jsonResponse),
          description: `Failed to save the converted JSON file.`,
          title: 'WOOT! FBA New Journey Save Failed',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }
      ordersWootRow.sourcePoJsonPath = jsonPath
    }catch(error){
      setDataError({
        content: JSON.stringify(error),
        description: `Failed to save the converted JSON file.`,
        title: 'WOOT! FBA New Journey Save Failed',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep('Saving WOOT! Journey to ordersWoot table.')
    
    try {
      const recordResponse = await apiFetch({
        endpoint: 'azureTables',
        method: 'POST',
        accessToken: userData.accessToken,
        body: JSON.stringify({
          table: 'ordersWoot',
          records: [ordersWootRow]
        })
      })
      if(!recordResponse.ok){
        setDataError({
          content: JSON.stringify(recordResponse),
          description: `Failed to save the record to the ordersWoot table.`,
          title: 'WOOT! FBA New Journey Save Failed',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }
    }catch(error){
      setDataError({
        content: JSON.stringify(error),
        description: `Failed to save the record to the ordersWoot table.`,
        title: 'WOOT! FBA New Journey Save Failed',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep('Mapping JSON to Allocation IAG.')
    await handleMapAllocationIag(ordersWootRow, false)
    
    toggleLoading(false)
    setLastRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  }, [userData])
  // # endregion

  // #region ALLOCATION PO
  const handleMapAllocationIag = useCallback(async (order: OrdersWootTableRow, doRefresh?: boolean) => {
    setDataError(undefined)
    changeStep('Requesting Mission Control Data')
    changeSubStep('Loading Product Data')
    toggleLoading(true)
    try {
      // Load Souce JSON Converted
      const allocationJsonResponse = await apiFetch<WootAllocationExcelRow[]>({
        endpoint: 'blob',
        method: 'GET',
        accessToken: userData.accessToken,
        params: [
          {key: 'location', value: 'dataservices'},
          {key: 'container', value: 'storage'},
          {key: 'blobname', value: order.sourcePoJsonPath},
          {key: 'json', value: 'true'}
        ]
      })
      if(!allocationJsonResponse.ok){
        setDataError({
          content: allocationJsonResponse.body,
          description: `Failed to get Source PO JSON. rowKey[${order.rowKey}]`,
          title: 'Allocation IAG Mapping Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }

      changeSubStep('Determining SKU/ASIN Uniques for Product and Inventory Queries')
      const uniqueSkus = []
      const uniqueASIN = []
      for(const row of allocationJsonResponse.body){
        const sku = row.ModelNumber?.trim().toLowerCase()
        const asin = row.ASIN?.trim()
        if(sku?.length > 0){
          if(!uniqueSkus.includes(sku)){
            uniqueSkus.push(sku)
          }
        }
        if(asin?.length > 0){
          if(!uniqueASIN.includes(asin)){
            uniqueASIN.push(asin)
          } 
        }
      }

      changeSubStep('Getting Product Inventory')
      const mpnIn = `'${uniqueSkus.join(`','`)}'`
      const asinIn = `'${uniqueASIN.join(`','`)}'`
      let asinQuery = ''
      if(uniqueASIN.length > 0){
        asinQuery = ` OR Product__r.ASIN__c IN (${asinIn})`
      }
      const soql = `SELECT Id, Name, Fulfillable_Inventory__c, Product__c, Product__r.MPN__c, Product__r.ASIN__c, Warehouse__r.Name FROM Inventory__c WHERE (Product__r.MPN__c IN (${mpnIn})${asinQuery}) AND Warehouse__r.Name IN ('FBA Woot', 'FBA US') AND Product__r.RecordType.Name = 'Product'`
      const inventoryResponse = await apiFetch<{records: Inventory__c[]}>({
        endpoint: 'salesforceQuery',
        method: 'POST',
        accessToken: userData.accessToken,
        body: JSON.stringify({
          soql
        })
      })
      if(!inventoryResponse.ok){
        setDataError({
          content: inventoryResponse.body,
          description: `Failed to get Inventory from Mission Control.`,
          title: 'Allocation IAG Mapping Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }

      changeSubStep('Mapping IAGs')
      const mapped = mapWootAllocationIAGs(order, allocationJsonResponse.body, inventoryResponse.body.records)
      
      changeSubStep('Saving Mapping Results')
      const saveResultBlobName = `${WOOT_FBA_CB_PATH}/${order.poNumber}/${order.poNumber}-allocation-mapped.json`
      const saveResultResponse = await apiFetch({
        endpoint: 'blob',
        method: 'POST',
        accessToken: userData.accessToken,
        body: JSON.stringify(mapped),
        params: [
          {key: 'location', value: 'dataservices'},
          {key: 'container', value: 'storage'},
          {key: 'blobname', value: saveResultBlobName}
        ]
      })
      if(!saveResultResponse.ok){
        setDataError({
          content: saveResultResponse.body,
          description: `Failed to save IAG mapped results file.`,
          title: 'Allocation IAG Mapping Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }

      changeSubStep('Saving Journey')
      //only modify the field in question in case other are modifying it
      const saveJourneyResponse = await apiFetch({
        endpoint: 'azureTables',
        method: 'POST',
        accessToken: userData.accessToken,
        body: JSON.stringify({
          table: 'ordersWoot',
          records: [
            {
              partitionKey: order.partitionKey, 
              rowKey: order.rowKey, 
              allocationIagMappedPath: 
              saveResultBlobName, 
              hasAllocationMappingErrors: mapped.validation.length > 0
            }
          ]
        })
      })
      if(!saveJourneyResponse.ok){
        setDataError({
          content: saveJourneyResponse.body,
          description: `Failed to save IAG mapped results record.`,
          title: 'Allocation IAG Mapping Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }
      if(!doRefresh || doRefresh === true){
        setLastRefresh(DateTime.now().setZone('America/Chicago').toLocaleString(DateTime.TIME_WITH_SECONDS))
      }

    }catch(error){
      setDataError({
        content: error,
        description: `Failed to map allocation IAG. ${JSON.stringify(error)}`,
        title: 'Allocation IAG Mapping Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    toggleLoading(false)
  }, [wootOrders, userData])
  // #endregion

  const handleCloseDataError = useCallback((e) => {
    setDataError({
      visible: false
    })
  }, [])

  // #region GLOBAL

  function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  const handleDownloadFile = useCallback((e) => {
    const download = async function() {
      if(!e.currentTarget.dataset.path){
        return
      }

      const justFileName = e.currentTarget.dataset.path?.split('/').pop()
      const justExtension = justFileName.split('.')?.pop().toLowerCase()

      setDataError(undefined)
      changeStep('Download File from Azure')
      changeSubStep(`Requesting data for ${e.currentTarget.dataset.path}`)
      toggleLoading(true)

      const response = await apiFetchRaw({
        endpoint: 'blob',
        method: 'GET',
        accessToken: userData.accessToken,
        params: [
          {key: 'location', value: 'dataservices'},
          {key: 'container', value: 'storage'},
          {key: 'blobname', value: e.currentTarget.dataset.path}
        ],
        fileExtension: justExtension
      })

      if(!response.ok){
        setDataError({
          content: JSON.stringify(response),
          description: `Failed download file ${justFileName} from Azure.`,
          title: 'Download Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }

      if (response.ok && response.body instanceof Blob) {
        downloadBlob(response.body, justFileName || 'download.xlsx');
      } else if(response.ok){
        downloadBlob(new Blob([response.body as any], { type: 'json' }), justFileName || 'download.xlsx');
      }
      toggleLoading(false)
    }
    download()
  }, [])

  const handlePreviewFile = useCallback((e) => {
    const preview = async function(){
      if(!e.currentTarget.dataset.path){
        return
      }

      const justFileName = e.currentTarget.dataset.path?.split('/').pop()
      setDataError(undefined)
      changeStep('Download File from Azure')
      changeSubStep(`Requesting data for ${e.currentTarget.dataset.path}`)
      toggleLoading(true)
      const response = await apiFetch({
        endpoint: 'blob',
        method: 'GET',
        accessToken: userData.accessToken,
        params: [
          {key: 'location', value: 'dataservices'},
          {key: 'container', value: 'storage'},
          {key: 'blobname', value: e.currentTarget.dataset.path}
        ]
      })

      if(!response.ok){
        setDataError({
          content: JSON.stringify(response),
          description: `Failed download file ${justFileName} from Azure.`,
          title: 'Download Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }

      setBlobModal({
        blobData: <JSONPretty data={response.body} theme={CUSTOM_JSON_THEME} style={{fontSize: '1.5em'}} />,
        blobName: justFileName,
        closeModal: () => {setBlobModal(undefined)},
        modalVisible: true,
        raw: JSON.stringify(response.body)
      } as BlobModalComponentProperties)

      toggleLoading(false)
    }
    preview()
  }, [])
  // #endregion

  // #region VIEW
  const view = useMemo(() => {
    const tabPanels = []
    const tabs = []
    for(const row of wootOrders ?? []){
      const totalSteps = 3
      let completeStatuses = 0
      let pendingStatuses = 0
      let errorStatuses = 0
      let notStarted = 0
      switch(row.allocationStatus){
        case 'complete':
          completeStatuses += 1
          break
        case 'error':
          errorStatuses += 1
          break
        case 'pending':
          pendingStatuses += 1
          break
        default: 
          notStarted += 1
          break
      }
      switch(row.returnStatus){
        case 'complete':
          completeStatuses += 1
          break
        case 'error':
          errorStatuses += 1
          break
        case 'pending':
          pendingStatuses += 1
          break
        default: 
          notStarted += 1
          break
      }
      switch(row.invoiceStatus){
        case 'complete':
          completeStatuses += 1
          break
        case 'error':
          errorStatuses += 1
          break
        case 'pending':
          pendingStatuses += 1
          break
        default: 
          notStarted += 1
          break
      }
      const completePercent = (completeStatuses / totalSteps) * 100;
      const pendingPercent = (pendingStatuses / totalSteps) * 100;
      const errorPercent = (errorStatuses / totalSteps) * 100;
      const notStartedPercent = (notStarted / totalSteps) * 100;

      const poColors = getStatusColors(row.status)
      const allocationColors = getStatusColors(row.allocationStatus)
      const returnColors = getStatusColors(row.returnStatus)
      const invoiceColors = getStatusColors(row.invoiceStatus)
      tabs.push(<Tab>
        <span className='beacon-header'>{row.poNumber}&nbsp;&nbsp;<Badge bg={poColors.bg} text={poColors.fg}>{row.status.toUpperCase()}</Badge></span>
      </Tab>)

      tabPanels.push(<TabPanel>
        <WootFbaJourneyHeader 
          allocationColors={allocationColors}
          completePercent={completePercent}
          errorPercent={errorPercent}
          notStartedPercent={notStartedPercent}
          invoiceColors={invoiceColors}
          pendingPercent={pendingPercent}
          poColors={poColors}
          returnColors={returnColors}
          row={row}
        />
        {/* ALLOCATION SECTION */}
        <WootFbaJourneyAllocation 
          handleDownloadFile={handleDownloadFile}
          handlePreviewFile={handlePreviewFile}
          handleMapAllocationIag={handleMapAllocationIag} 
          allocationColors={allocationColors}
          setDataError={setDataError}
          setLastRefresh={setLastRefresh}
          userData={userData}
          wootOrders={wootOrders}
          row={row} 
        />
        {/* RETURN SECTION */}
        <WootFbaJourneyReturnPO
          handleDownloadFile={handleDownloadFile}
          handlePreviewFile={handlePreviewFile}
          returnColors={returnColors}
          userData={userData}
          row={row}
          setLastRefresh={setLastRefresh}
        />
        {/* INVOICE SECTION */}
        <WootFbaJourneyInvoice
          handleDownloadFile={handleDownloadFile}
          handlePreviewFile={handlePreviewFile}
          invoiceColors={invoiceColors}
          userData={userData}
          row={row}
          setLastRefresh={setLastRefresh}
        />
      </TabPanel>)
    }

    return (
      <div>
        <BlobModalComponent 
          blobData={blobModal?.blobData} 
          blobName={blobModal?.blobName}
          closeModal={blobModal?.closeModal} 
          modalVisible={blobModal?.modalVisible ?? false}
          raw={blobModal?.raw}
        />
        <CustomError closeError={handleCloseDataError} content={dataError?.content} description={dataError?.description} title={dataError?.title} visible={dataError?.visible} />
          <WootFbaNewJourney handleNewJourneySave={handleNewJourneySave} />
          <hr/>
          <span onClick={() => {setLastRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))}} title='Refresh Journeys' style={{cursor: 'pointer'}}>
            <RefreshSvg height={25}/>
          </span>
          <span className='beacon-label'> as of {lastRefresh}</span>
          <br/>
          <Tabs>
            <TabList>
              {tabs}
            </TabList>
            {tabPanels}
          </Tabs>
      </div>
    )
  }, [newPONumber, wootOrders, dataError, showComplete, blobModal])

  return view
}